import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CubeIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  TagIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../services/api';
import toast from 'react-hot-toast';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/relatorios/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Erro ao carregar dados do dashboard</p>
      </div>
    );
  }

  // Dados para o gráfico de categorias
  const categoriasChartData = dashboardData.categorias.map(cat => ({
    name: cat.nome,
    value: cat._count.itens
  }));

  // Dados para o gráfico de movimentações
  const movimentacoesChartData = [
    { name: 'Entradas', value: dashboardData.movimentacoesEntrada },
    { name: 'Saídas', value: dashboardData.movimentacoesSaida },
    { name: 'Ajustes', value: dashboardData.movimentacoesAjuste }
  ];

  return (
    <div className="space-y-6">
      {/* Título */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Visão geral do sistema de estoque
        </p>
      </div>

      {/* Cards de estatísticas */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CubeIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total de Itens
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {dashboardData.totalItens}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Estoque Baixo
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {dashboardData.itensEstoqueBaixo}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Valor Total
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    R$ {dashboardData.valorTotalEstoque.toLocaleString('pt-BR')}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TagIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Categorias
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {dashboardData.categorias.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Gráfico de categorias */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Itens por Categoria</h3>
          </div>
          <div className="card-body">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoriasChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoriasChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Gráfico de movimentações */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Movimentações {dashboardData.periodo.descricao}</h3>
          </div>
          <div className="card-body">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={movimentacoesChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Últimas movimentações */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Últimas Movimentações</h3>
            <Link
              to="/movimentacoes"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Ver todas
            </Link>
          </div>
        </div>
        <div className="card-body">
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {dashboardData.ultimasMovimentacoes.map((movimentacao) => (
                <li key={movimentacao.id} className="py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {movimentacao.tipo === 'ENTRADA' ? (
                        <ArrowUpIcon className="h-5 w-5 text-success-600" />
                      ) : movimentacao.tipo === 'AJUSTE' ? (
                        <CogIcon className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ArrowDownIcon className="h-5 w-5 text-danger-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {movimentacao.item.nome}
                      </p>
                      <p className="text-sm text-gray-500">
                        {movimentacao.motivo} • {movimentacao.user.name}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-sm text-gray-500">
                      {new Date(movimentacao.data).toLocaleDateString('pt-BR')}
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        movimentacao.tipo === 'ENTRADA' 
                          ? 'bg-success-100 text-success-800'
                          : movimentacao.tipo === 'AJUSTE'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-danger-100 text-danger-800'
                      }`}>
                        {movimentacao.quantidade} {movimentacao.item.unidade}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
} 